import ReactMarkdown from "react-markdown";
import LPriceBox from "../lprice-box/LPriceBox";
import { ComponentCmsDataPriceItem } from "@/generated";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import LCountDownTimer from "../lcount-down-timer/LCountDownTimer";
import parse from "html-react-parser";

/* eslint-disable-next-line */
export interface LPriceSectionProps {
  id?: string;
  topTitle?: string;
  topTitleColor?: string;
  mainTitle?: string;
  mainTitleColor?: string;
  description?: string; //rich text
  descriptionColor?: string;
  priceItems?: ComponentCmsDataPriceItem[];
  sectionId?: string;
  showAsSlider?: boolean;
  showAsSliderWeb?: boolean;
  priceItemsPerSlideWeb?: number | null;
  countDownTimer?: {
    endDate: Date;
    description?: string;
  };
  urlParams?: string;
}

export function LPriceSection(props: LPriceSectionProps) {
  const [showYearly, setShowYearly] = useState<boolean>(false);

  const mergeQueryParams = (
    additionalParams: string,
    baseUrl?: string | null
  ) => {
    if (baseUrl) {
      const url = new URL(baseUrl);
      const params: any = new URLSearchParams(additionalParams || "");

      for (const [key, value] of params) {
        url?.searchParams?.append(key, value);
      }

      return url?.toString();
    }

    return "";
  };

  const [activeTab, setActiveTab] = useState("ANNUAL");

  return (
    <div className="bg-eklight" id={props?.sectionId || props?.id}>
      <div className="container flex flex-col px-0 py-20 mx-auto gap-7 lg:max-w-none">
        <div
          style={{ color: props?.topTitleColor }}
          className={`text-xl font-semibold text-center ${props?.topTitleColor ? "" : "text-ekgreen"}`}
        >
          {props?.topTitle}
        </div>
        <h1 className="text-center" style={{ color: props?.mainTitleColor }}>
          {parse(props?.mainTitle ?? "")}
        </h1>
        {props?.description && (
          <div
            className="text-center"
            style={{ color: props?.descriptionColor }}
          >
            <ReactMarkdown
              // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
              // eslint-disable-next-line react/no-children-prop
              children={props?.description}
            />
          </div>
        )}
        {props?.countDownTimer && (
          <div className="flex justify-center w-full">
            <LCountDownTimer
              endDate={new Date(props?.countDownTimer?.endDate)}
              description={props?.countDownTimer?.description}
            />
          </div>
        )}

        <div className="w-full">
          <div
            className={clsx([
              "flex w-full lg:gap-8 mt-5 justify-start lg:pl-4",
              // Mobile slider settings
              props?.showAsSlider &&
                "md:flex-wrap flex-nowrap overflow-x-auto scroll-smooth snap-x snap-mandatory",
              // Desktop settings
              props?.showAsSliderWeb
                ? "lg:flex-nowrap lg:overflow-x-auto lg:px-16 lg:scroll-pl-16"
                : props?.priceItemsPerSlideWeb &&
                    props?.priceItemsPerSlideWeb > 0
                  ? `lg:grid lg:grid-cols-${props?.priceItemsPerSlideWeb}`
                  : "",
            ])}
          >
            {props?.priceItems?.map((priceTable, index) => {
              if (
                priceTable?.itemTopTitle &&
                [activeTab, "SIMPLE"]?.includes(priceTable?.itemTopTitle)
              ) {
                return (
                  <motion.div
                    className={clsx([
                      "snap-center snap-always",
                      // Mobile slider item width
                      props?.showAsSlider && "min-w-[85vw] md:min-w-0 px-4",
                      // Desktop slider item width (3 per view when in slider mode)
                      props?.showAsSliderWeb
                        ? "lg:w-1/3 lg:px-4 w-full"
                        : "w-full",
                      // General spacing
                      "flex-shrink-0",
                    ])}
                    initial={{ opacity: 0, y: -50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    exit={{ opacity: 0, y: 50 }}
                    transition={{ type: "spring", delay: index * 0.13 }}
                    key={priceTable?.id}
                  >
                    <LPriceBox
                      key={priceTable?.id}
                      tableTopTitle={priceTable?.itemTopTitle}
                      tableTopTitleColor={priceTable?.itemTopTitleColor}
                      tableMainTitle={priceTable?.itemMainTitle}
                      tableMainTitleColor={priceTable?.itemMainTitleColor}
                      tableDescription={priceTable?.itemDescription}
                      monthylRegularPrice={priceTable?.monthlyRegularPrice}
                      monthlyDiscountedPrice={
                        priceTable?.monthlyDiscountedPrice
                      }
                      monthlyUnderPriceText={priceTable?.monthlyUnderPriceText}
                      yearlyRegularPrice={priceTable?.yearlyRegularPrice}
                      yearlyDiscountedPrice={priceTable?.yearlyDiscountedPrice}
                      yearlyUnderPriceText={priceTable?.yearlyUnderPriceText}
                      tableButton={{
                        text: priceTable?.buttonText,
                        url: props?.urlParams
                          ? mergeQueryParams(
                              props?.urlParams,
                              priceTable?.buttonUrl
                            )
                          : priceTable?.buttonUrl,
                      }}
                      priceBenefits={priceTable?.priceBenefits?.map(
                        (benefit, idx) => {
                          return {
                            text: benefit?.text,
                            icon: benefit?.icon as IconName,
                            isGryedOut: benefit?.isGrayedOut,
                            isBold: benefit?.isBold,
                            isIndented: benefit?.isIndented,
                          };
                        }
                      )}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      // highlightTag={priceTable?.highlightTag}
                      highlightTag={
                        ["MONTH", "ANNUAL"]?.includes(priceTable?.itemTopTitle)
                          ? "Yes"
                          : undefined
                      }
                      showYearly={showYearly}
                    />
                  </motion.div>
                );
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LPriceSection;
